import { getMaterials } from './materials'
import { applyPartOverrides, getFormulaConstants } from '../utils/pricingConfig'
import advantageParts from '../data/advantage/parts'
import integrityParts from '../data/integrity/parts'

export const getPartsDataset = (type, pricingConfig) => {
  let parts

  switch(type) {
    case 'integrity':
    case 'integrity-cattle':
      parts = integrityParts
      break
    case 'advantage':
    default:
      parts = advantageParts
  }

  parts = parts.map(a => {return {...a}})

  return applyPartOverrides(parts, pricingConfig)
}

export const getParts = (part, type, values, pricingConfig) => {
  if (part) {
    if (!Array.isArray(part)) part = [part]
    part = part.map(p => getPartObject(p)).filter(n => n)
    if (!part.length) return []
  }

  if (!values) values = {}

  return (part ? part : getPartsDataset(type, pricingConfig)).map(p => {
    p.weight = getPartWeight(p, type, values, pricingConfig)
    p.galv = getPartGalv(p, type, values, pricingConfig)
    p.steelMrk = getPartSteelMrk(p, type, values)
    p.steelDrop = getPartSteelDrop(p, type, values, pricingConfig)
    p.steelCost = getPartSteelCost(p, type, values, pricingConfig)
    p.fastener = getPartFastener(p, type, values, pricingConfig)
    p.cover = getPartCover(p, type, values, pricingConfig)
    p.totalMrk = getPartTotalMrk(p, type, values, pricingConfig)

    p.materials = Array.from([...new Map(p.material.map(m => [m.id, m])).values()]).map(m => {
      const mat = getMaterials(m.id, type, pricingConfig)
      if (mat.length) return mat[0]
      return null
    })

    return p
  })
}

export const getPartsByPartNumber = (partNumbers, type, values, pricingConfig) => {
  let foundParts = null

  if (partNumbers) {
    if (!Array.isArray(partNumbers)) partNumbers = [partNumbers]
    foundParts = getPartsDataset(type, pricingConfig).filter(p => partNumbers.includes(p.partNumber))
    if (!foundParts.length) return []
  }

  return getParts(foundParts, type, values, pricingConfig)
}

export const getPartByPartNumber = (partNumber, type, values, pricingConfig) => {
  let foundParts = getPartsByPartNumber(partNumber, type, values, pricingConfig)
  if (foundParts.length) return foundParts[0]
  return false
}

export const getPartObject = (part, type, pricingConfig) => {
  if (!part) return getParts(null, type, undefined, pricingConfig)

  if (typeof part === 'object' && !Array.isArray(part) && part.id) {
    return part
  }

  part = getPartsDataset(type, pricingConfig).find(p => p.id === part)

  if (part) return part
  return false
}

export const getPartWeight = (part, type, values, pricingConfig) => {
  let weight = calculatePartMultipliers('weight', 'poundsPerUnit', part, type, values, pricingConfig)

  if (weight && part.galv !== false) {
    weight *= 1.06
  }

  return weight
}

export const getPartGalv = (part, type, values, pricingConfig) => {
  if (part.galv !== null) {
    return part.galv
  }

  part = getPartObject(part, type, pricingConfig)

  if (!part || part.galv === false) {
    return 0
  }

  if (!part.weight) {
    part.weight = getPartWeight(part, type, values, pricingConfig)
    if (!part.weight) part.weight = 0
  }

  const { galvRate } = getFormulaConstants(pricingConfig)
  const galv = part.weight * galvRate

  return galv
}

export const getPartSteelMrk = () => {
  return 0
}

export const getPartSteelDrop = (part, type, values, pricingConfig) => {
  let steelDrop = calculatePartMultipliers('steelDrop', 'pricePerUnit', part, type, values, pricingConfig)
  return steelDrop
}

export const getPartSteelCost = (part, type, values, pricingConfig) => {
  let steelCost = calculatePartMultipliers('steelCost', 'pricePerUnit', part, type, values, pricingConfig)
  return steelCost
}

export const getPartFastener = (part, type, values, pricingConfig) => {
  let fastener = calculatePartMultipliers('fastener', 'pricePerUnit', part, type, values, pricingConfig)
  return fastener
}

export const getPartCover = (part, type, values, pricingConfig) => {
  let cover = calculatePartMultipliers('cover', 'pricePerUnit', part, type, values, pricingConfig)
  return cover
}

export const getPartTotalMrk = (part, type, values, pricingConfig) => {
  part = getPartObject(part, type, pricingConfig)

  if (part === false) return 0

  // If totalMrk is explicitly set in the data, use that value instead of calculating
  if (part.totalMrk !== null && part.totalMrk !== undefined) {
    return part.totalMrk
  }

  part.galv = part.galv ?? getPartGalv(part, type, values, pricingConfig) ?? 0
  part.steelMrk = part.steelMrk ?? getPartSteelMrk(part, type, values) ?? 0
  part.steelDrop = part.steelDrop ?? getPartSteelDrop(part, type, values, pricingConfig) ?? 0
  part.steelCost = part.steelCost ?? getPartSteelCost(part, type, values, pricingConfig) ?? 0
  part.fastener = part.fastener ?? getPartFastener(part, type, values, pricingConfig) ?? 0
  part.cover = part.cover ?? getPartCover(part, type, values, pricingConfig) ?? 0

  const totalMrk = part.galv + part.steelMrk + part.steelDrop + part.steelCost + part.fastener + part.cover

  return totalMrk
}

export const calculatePartMultipliers = (field, numberField, part, type, values, pricingConfig) => {
  if (part[field] !== null) {
    return part[field]
  }

  part = getPartObject(part, type, pricingConfig)

  if (!part || part[field] === false || !part.material || !part.material.length) {
    return false
  }

  const multiplierField = field + 'Multiplier'
  let value = 0

  part.material.forEach(m => {
    if (!Object.prototype.hasOwnProperty.call(m, multiplierField) || m[multiplierField] === false) {
      return false
    }

    let material = getMaterials(m.id, type, pricingConfig)

    if (material.length) {
      material = material[0]
    } else {
      return false
    }

    if (!Array.isArray(m[multiplierField])) {
      m[multiplierField] = [m[multiplierField]]
    }

    let add = material[numberField]

    m[multiplierField].forEach(multi => {
      if (multi === null) multi = 1

      if (Array.isArray(multi)) {
        let subAdd = 0

        multi.forEach(subMulti => {
          if (typeof subMulti === 'string') {
            if (values[subMulti]) {
              subAdd += values[subMulti]
            }
          } else {
            subAdd += subMulti
          }
        })

        if (subAdd) add *= subAdd
      } else if (typeof multi === 'object' && multi !== null) {
        if (values[multi.field] && values[multi.field] === multi.fieldValue) {
          if (Object.prototype.hasOwnProperty.call(multi, 'trueMultiplier')) {
            if (multi.trueMultiplier === null) multi.trueMultiplier = 1
            add *= multi.trueMultiplier
          } else if (Object.prototype.hasOwnProperty.call(multi, 'trueValue')) {
            add = 0
            value = multi.trueValue
          }
        } else {
          if (Object.prototype.hasOwnProperty.call(multi, 'falseMultiplier')) {
            if (multi.falseMultiplier === null) multi.falseMultiplier = 1
            add *= multi.falseMultiplier
          } else if (Object.prototype.hasOwnProperty.call(multi, 'falseValue')) {
            add = 0
            value = multi.falseValue
          }
        }
      } else {
        add *= multi
      }
    })

    value += add
  })

  return value
}
